import { contrastColor, shade } from '@mentimeter/ragnar-colors';
import { type PropsWithChildren } from 'react';
import { MoreIcon } from '@mentimeter/ragnar-visuals';
import { getAvatarsToRender } from './utils';
import type { AvatarStackProps } from './types';

const NUMBER_OF_VISIBLE_AVATARS = 3;
const AVATAR_SHIFT_MULTIPLIER = 1.6;

type ColorShade = 'regular' | 'hovering';

const COLOR_SHADES: Record<ColorShade, number> = {
  regular: 0.64,
  hovering: 0.8,
};

export interface AvatarStackSvgProps extends AvatarStackProps {
  x: number;
  y: number;
  color: string;
  colorShade: 'regular' | 'hovering';
  radius: number;
  stroke?: string;
  opacity?: number;
}

export const AvatarStackSvg = ({
  identities,
  x,
  y,
  color,
  colorShade,
  radius,
  stroke,
  opacity,
  maxNumberOfVisibleAvatars = NUMBER_OF_VISIBLE_AVATARS,
  totalVotes,
}: AvatarStackSvgProps) => {
  if (!identities.length) {
    return null;
  }
  const { numberOfVisibleAvatars, numberOfExtraAvatars } = getAvatarsToRender(
    identities.length,
    maxNumberOfVisibleAvatars,
  );
  const avatarsToRender = identities.slice(0, numberOfVisibleAvatars);
  const hasReachedIdentityLimit = totalVotes > identities.length;

  const xShift = radius * AVATAR_SHIFT_MULTIPLIER;
  const shadeAmount = COLOR_SHADES[colorShade];
  const fill = shade(color, '#FFFFFF', shadeAmount);
  const borderColor = stroke ?? color;
  const extraAvatarColor = shade(color, '#FFFFFF', shadeAmount / 2);

  return (
    <g opacity={opacity} data-testid="avatar-stack">
      {avatarsToRender.map((avatar, i) => (
        <AvatarCircle
          key={avatar.id ?? i}
          x={x + i * xShift}
          y={y}
          fill={fill}
          stroke={borderColor}
          r={radius}
        >
          <AvatarText fontSize={radius * 0.9} color={contrastColor(fill)}>
            {avatar.displayName.charAt(0).toUpperCase()}
          </AvatarText>
        </AvatarCircle>
      ))}
      {numberOfExtraAvatars > 0 && (
        <AvatarCircle
          key="extra-avatars"
          x={x + numberOfVisibleAvatars * xShift}
          y={y}
          fill={shade(color, '#FFFFFF', shadeAmount / 2)}
          stroke={borderColor}
          r={radius}
        >
          {hasReachedIdentityLimit ? (
            <g
              data-testid="more-icon"
              transform={`translate(${-radius / 2}, ${-radius / 2})`}
            >
              <MoreIcon size={radius} color={contrastColor(extraAvatarColor)} />
            </g>
          ) : (
            <AvatarText
              fontSize={radius * 0.9}
              color={contrastColor(extraAvatarColor)}
            >
              +{numberOfExtraAvatars}
            </AvatarText>
          )}
        </AvatarCircle>
      )}
    </g>
  );
};

const AvatarCircle = ({
  children,
  x,
  y,
  fill,
  stroke,
  r,
}: PropsWithChildren<{
  x: number;
  y: number;
  fill: string;
  stroke: string;
  r: number;
}>) => {
  return (
    <g data-testid="avatar" transform={`translate(${x}, ${y})`}>
      <circle
        r={r}
        stroke={stroke}
        strokeWidth={r * 0.12}
        fill={fill}
        style={{
          transition: 'fill 0.2s',
        }}
      />
      {children}
    </g>
  );
};

const AvatarText = ({
  children,
  color,
  fontSize,
}: PropsWithChildren<{
  color: string;
  fontSize: number;
}>) => {
  return (
    <text
      fontSize={fontSize}
      fill={color}
      dominantBaseline="central"
      textAnchor="middle"
    >
      {children}
    </text>
  );
};
